import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Flower2, Gem, Play } from "lucide-react";
import { products, collections, productsWithVideo, formatINR } from "@/data/products";
import ProductCard from "@/components/product/ProductCard";
import VideoPlayer from "@/components/product/VideoPlayer";

const BOX_MOCKUP =
  "https://res.cloudinary.com/drcoefnzk/image/upload/v1783795280/PHOTO-2026-03-31-13-37-20_dvz1wa.jpg";

// Hero collage assets (Cloudinary — new brand imagery)
const HERO_TILES = {
  topLeft: "https://res.cloudinary.com/drcoefnzk/image/upload/v1784901096/WhatsApp_Image_2026-07-24_at_5.41.35_PM_2_nwhwfw.jpg",     // Amber pendant on stone
  topRight: "https://res.cloudinary.com/drcoefnzk/image/upload/v1784901095/WhatsApp_Image_2026-07-24_at_5.41.35_PM_1_zcfgoq.jpg",    // Model side portrait
  botLeft: "https://res.cloudinary.com/drcoefnzk/image/upload/v1784901097/WhatsApp_Image_2026-07-24_at_6.03.40_PM_wlbzkj.jpg",       // Amethyst raw crystal
  botCenter: "https://res.cloudinary.com/drcoefnzk/image/upload/v1784901096/WhatsApp_Image_2026-07-24_at_6.03.37_PM_htexhn.jpg",     // Rose quartz raw stone
  botRight: "https://res.cloudinary.com/drcoefnzk/image/upload/v1784901013/WhatsApp_Image_2026-07-24_at_5.41.35_PM_t2exwg.jpg",      // Aquamarine earrings on display
  extra: "https://res.cloudinary.com/drcoefnzk/image/upload/v1784900975/WhatsApp_Image_2026-07-24_at_5.41.34_PM_po5gl8.jpg",         // Earrings hanging from branch
};
const LOGO_URL =
  "https://res.cloudinary.com/drcoefnzk/image/upload/e_trim:10/f_auto,q_auto/v1783795248/Innayaa_Box_Logo_3.5x3.5_elmi29.png";

export default function Home() {
  const earrings = products.filter((p) => p.category === "Earrings");
  const pendants = products.filter((p) => p.category === "Pendants");
  const newArrivals = products.filter((p) => p.isNew).slice(0, 4);
  const featuredEarrings = earrings.slice(0, 8);
  const videos = productsWithVideo().slice(0, 3);

  return (
    <div data-testid="home-page" className="bg-[#FAF8F5]">
      {/* HERO — Collage grid with logo card at center */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 md:px-10 lg:px-16 pt-6 md:pt-10 pb-12 md:pb-20">
          <p className="overline text-center">The Crystal Edit · Spring Bloom</p>

          {/* 3-col × 2-row collage — same structure on mobile & desktop */}
          <div className="mt-5 md:mt-8 grid grid-cols-[1fr_1.35fr_1fr] grid-rows-2 gap-1.5 sm:gap-2 md:gap-3">
            {/* Top-left */}
            <div className="relative overflow-hidden bg-[#EDE4D3] aspect-square">
              <img
                src={HERO_TILES.topLeft}
                alt="Innayaa pendant on stone"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out hover:scale-[1.04]"
              />
            </div>

            {/* Center — Logo card */}
            <div className="relative overflow-hidden bg-[#EDE4D3] flex flex-col items-center justify-center px-3 py-4 md:px-8 md:py-8">
              <div className="absolute inset-0 bg-gradient-to-br from-[#F0EBE1] via-[#EDE4D3] to-[#E6DBC5]" />
              {/* Subtle grain */}
              <div className="absolute inset-0 opacity-20 mix-blend-multiply pointer-events-none"
                   style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />
              <div className="relative flex flex-col items-center text-center max-w-[94%]">
                <img
                  src={LOGO_URL}
                  alt="Innayaa"
                  className="w-16 sm:w-20 md:w-32 lg:w-40 h-auto object-contain"
                />
                <p className="mt-3 md:mt-5 font-serif italic text-[13px] sm:text-[15px] md:text-xl lg:text-2xl leading-snug tracking-wide text-[#5C564F]">
                  where elegance<br />
                  meets artistry
                </p>
              </div>
            </div>

            {/* Top-right */}
            <div className="relative overflow-hidden bg-[#EDE4D3] aspect-square">
              <img
                src={HERO_TILES.topRight}
                alt="Innayaa model wearing earrings"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out hover:scale-[1.04]"
              />
            </div>

            {/* Bottom-left */}
            <div className="relative overflow-hidden bg-[#EDE4D3] aspect-square">
              <img
                src={HERO_TILES.botLeft}
                alt="Amethyst raw crystal"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out hover:scale-[1.04]"
              />
            </div>

            {/* Bottom-center (wide-ish landscape — same cell size but different image) */}
            <div className="relative overflow-hidden bg-[#EDE4D3] aspect-square">
              <img
                src={HERO_TILES.botCenter}
                alt="Rose quartz stone"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out hover:scale-[1.04]"
              />
            </div>

            {/* Bottom-right */}
            <div className="relative overflow-hidden bg-[#EDE4D3] aspect-square">
              <img
                src={HERO_TILES.botRight}
                alt="Aquamarine earrings on display"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out hover:scale-[1.04]"
              />
            </div>
          </div>

          {/* Headline + CTAs below the collage */}
          <div className="mt-10 md:mt-16 max-w-3xl mx-auto text-center">
            <h1 className="font-serif text-[36px] sm:text-[48px] lg:text-[64px] leading-[1.05] tracking-tight text-[#2A2626]">
              Quiet beauty,<br className="sm:hidden" />
              <span className="italic font-light"> crystallised.</span>
            </h1>
            <p className="mt-4 md:mt-6 text-[14px] md:text-base leading-relaxed text-[#5C564F] max-w-lg mx-auto">
              Handcrafted earrings and pendants shaped from semi-precious stones
              and freshwater pearls — designed to carry the calm energy of
              crystals with a modern, minimal silhouette.
            </p>
            <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-6">
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="inline-flex items-center bg-[#2A2626] text-[#FAF8F5] px-7 md:px-9 py-3.5 md:py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
              >
                Shop the collection
              </Link>
              <Link
                to="/about"
                data-testid="hero-story-cta"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#2A2626] link-underline pb-1"
              >
                Our Story <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STONE STRIP */}
      <section className="border-y border-[#DCD5CA] bg-[#F0EBE1]/60 overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap py-4 md:py-5">
          {[...Array(3)].map((_, group) => (
            <div key={group} className="flex items-center gap-10 md:gap-14 mx-10 md:mx-14 text-[#2A2626]">
              {[
                "Aquamarine",
                "Garnet",
                "Freshwater Pearl",
                "Opal",
                "Peridot",
                "Rose Quartz",
                "Smoky Quartz",
                "Blue Topaz",
                "Prehnite",
                "Citrine",
                "Amethyst",
                "Moonstone",
              ].map((stone) => (
                <span key={`${group}-${stone}`} className="font-serif italic text-xl md:text-3xl tracking-tight opacity-90">
                  {stone} <span className="mx-4 md:mx-6 opacity-40">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED VIDEO — Amethyst Echo Pendant */}
      <section className="bg-[#F0EBE1]/60 border-y border-[#DCD5CA]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-16 md:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <p className="overline">Featured · In Motion</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#2A2626]">
                The Amethyst<br />
                <span className="italic">Echo Pendant.</span>
              </h2>
              <p className="mt-5 md:mt-6 text-[15px] leading-relaxed text-[#5C564F] max-w-md">
                A handcrafted pendant featuring a natural raw Amethyst crystal — its
                organic form catching the light in shifting flashes of violet. Watch
                the light move across the stone.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-5 md:gap-6">
                <Link
                  to="/product/amethyst-echo"
                  data-testid="featured-video-shop-cta"
                  className="inline-flex items-center bg-[#2A2626] text-[#FAF8F5] px-7 md:px-9 py-3.5 md:py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
                >
                  Shop this piece
                </Link>
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#5C564F]">
                  ₹499 · Amethyst
                </span>
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative bg-[#2A2626] overflow-hidden aspect-[9/16] max-w-[420px] mx-auto lg:ml-auto lg:mr-0 shadow-2xl">
                <video
                  data-testid="featured-video-player"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster="https://res.cloudinary.com/drcoefnzk/image/upload/v1784842425/Amethyst_Echo_pendant__v4y5xo.png"
                  ref={(el) => {
                    if (el && el.paused) {
                      const p = el.play();
                      if (p && typeof p.catch === "function") p.catch(() => {});
                    }
                  }}
                  className="absolute inset-0 w-full h-full object-cover"
                >
                  <source
                    src="https://res.cloudinary.com/drcoefnzk/video/upload/f_mp4,vc_h264,q_auto/v1784901857/Amethyst_Echo_pendant_tc4gmu.mp4"
                    type="video/mp4"
                  />
                  <source
                    src="https://res.cloudinary.com/drcoefnzk/video/upload/v1784901857/Amethyst_Echo_pendant_tc4gmu.mov"
                    type="video/quicktime"
                  />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-16 md:py-24 lg:py-32">
        <div className="flex items-end justify-between mb-8 md:mb-12 lg:mb-16">
          <div>
            <p className="overline">New Drop</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#2A2626]">
              Fresh from the atelier
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="new-arrivals-view-all"
            className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#2A2626] link-underline pb-1"
          >
            View all <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-8 md:gap-y-16">
          {newArrivals.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* SHOP BY CATEGORY — Two-tile bento (the ONLY two product lines) */}
      <section className="bg-[#F0EBE1]/70 py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
          <div className="max-w-2xl">
            <p className="overline">Shop the House</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2A2626]">
              Two lines. One quiet ritual.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-10 md:mt-14">
            {collections.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`collection-card-${c.slug}`}
                className="relative group overflow-hidden bg-[#EDE4D3] min-h-[420px] md:min-h-[600px]"
              >
                <img
                  src={c.cover}
                  alt={c.name}
                  className="w-full h-full object-cover absolute inset-0 transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A2626]/70 via-[#2A2626]/10 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-10 flex flex-col justify-end text-[#FAF8F5]">
                  <p className="overline text-[#FAF8F5]/80">The {c.name} Line</p>
                  <h3 className="mt-2 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight">
                    {c.name}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-[#FAF8F5]/90 max-w-md">
                    {c.tagline}
                  </p>
                  <span className="mt-5 md:mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] opacity-90 group-hover:opacity-100">
                    Explore {c.name}
                    <ArrowRight size={12} strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO FILM STRIP — showcase short videos */}
      {videos.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="flex items-end justify-between mb-8 md:mb-12">
            <div>
              <p className="overline">In Motion</p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2A2626]">
                See them come to life.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {videos.map((p) => (
              <div
                key={p.id}
                data-testid={`video-tile-${p.slug}`}
                className="group block"
              >
                <div className="relative aspect-[9/16] overflow-hidden bg-[#2A2626]">
                  <VideoPlayer
                    videoId={p.videoId}
                    label={p.name}
                    poster={p.images.lifestyle || p.images.closeUp || p.images.hero}
                    className="aspect-[9/16]"
                  />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#2A2626]/85 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-[#FAF8F5] pointer-events-none">
                    <div className="flex items-center gap-2 opacity-90 text-[10px] uppercase tracking-[0.24em]">
                      <Play size={12} strokeWidth={1.5} /> Watch
                    </div>
                    <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tight">{p.name}</h3>
                    <div className="mt-1 flex items-baseline justify-between">
                      <p className="text-sm opacity-85">{formatINR(p.price)}</p>
                      <Link
                        to={`/product/${p.slug}`}
                        className="pointer-events-auto text-[10px] uppercase tracking-[0.22em] opacity-85 hover:opacity-100 underline underline-offset-4"
                      >
                        View →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CRAFTSMANSHIP — Story + Box mockup */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-16 md:py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 relative">
            <img
              src={BOX_MOCKUP}
              alt="Innayaa gift box"
              className="w-full aspect-[4/5] object-cover"
            />
            <div className="hidden md:block absolute -bottom-8 right-6 w-40 md:w-48 aspect-square overflow-hidden">
              <img
                src={products.find((p) => p.slug === "amber-pearl-earrings").images.closeUp}
                alt="Amber Pearl detail"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-8">
            <p className="overline">Made by hand · One pair at a time</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2A2626] leading-tight">
              A quiet ritual of<br />
              <span className="italic">stones, silver &amp; patience.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[#5C564F] max-w-lg">
              Every Innayaa piece begins with a stone — chosen for its light, its
              story, its energy. Delicately wired by hand and finished with 925
              sterling silver ear hooks, each pair carries the imperfect
              perfection of things truly made, not manufactured.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: Gem, label: "Semi-precious", sub: "Natural stones" },
                { icon: Flower2, label: "Handmade", sub: "In small batches" },
                { icon: Sparkles, label: "925 Silver", sub: "Ear hooks" },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={label} className="border-t border-[#DCD5CA] pt-4">
                  <Icon strokeWidth={1.25} size={22} className="text-[#2A2626]" />
                  <p className="mt-3 font-serif text-lg text-[#2A2626]">{label}</p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C564F] mt-1">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EARRINGS GRID */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pb-16 md:pb-24 lg:pb-32">
        <div className="flex items-end justify-between mb-8 md:mb-12">
          <div>
            <p className="overline">The Earring Edit</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2A2626]">Bestselling earrings</h2>
          </div>
          <Link
            to="/shop/earrings"
            className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#2A2626] link-underline pb-1"
          >
            All earrings <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-8 md:gap-y-16">
          {featuredEarrings.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* PENDANT LINE — showcase pendants with dark warm section */}
      <section className="bg-[#2A2626] text-[#FAF8F5] py-16 md:py-24 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
          <div className="flex items-end justify-between mb-8 md:mb-12">
            <div>
              <p className="overline text-[#FAF8F5]/70">Everyday Companion</p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#FAF8F5]">
                The Pendant Line
              </h2>
              <p className="mt-3 text-sm text-[#FAF8F5]/70 max-w-md">
                Natural crystals on premium waxed cord — meaningful pieces that carry you through every day.
              </p>
            </div>
            <Link
              to="/shop/pendants"
              className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#FAF8F5] link-underline pb-1"
            >
              Shop pendants <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {pendants.slice(0, 10).map((p, i) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                data-testid={`pendant-card-${p.slug}`}
                className="group block fade-up"
                style={{ animationDelay: `${(i % 5) * 60}ms` }}
              >
                <div className="relative aspect-square bg-[#FAF8F5]/5 overflow-hidden">
                  <img
                    src={p.images.plain}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="mt-3 md:mt-4 font-serif text-base md:text-lg text-[#FAF8F5] leading-tight">
                  {p.name}
                </p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#FAF8F5]/60 mt-1">
                  {formatINR(p.price)}
                </p>
              </Link>
            ))}
          </div>
          <div className="mt-8 md:hidden">
            <Link
              to="/shop/pendants"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#FAF8F5] link-underline pb-1"
            >
              Shop all pendants <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* INSTAGRAM LOOKBOOK — mix of lifestyle shots */}
      <section className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-10 lg:px-16 pb-16 md:pb-24">
        <div className="flex items-end justify-between mb-8 md:mb-10 px-2 md:px-0">
          <div>
            <p className="overline">@innayaa on Instagram</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl md:text-4xl tracking-tight text-[#2A2626]">The Lookbook</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
          {[
            earrings[0]?.images.lifestyle,
            earrings[3]?.images.hero,
            earrings[5]?.images.closeUp,
            earrings[10]?.images.lifestyle,
            earrings[7]?.images.hero,
            earrings[2]?.images.lifestyle,
            earrings[9]?.images.closeUp,
            earrings[12]?.images.lifestyle,
            earrings[8]?.images.hero,
            earrings[13]?.images.lifestyle,
          ].filter(Boolean).map((src, i) => (
            <div key={i} className="aspect-square overflow-hidden bg-[#F0EBE1] group">
              <img
                src={src}
                alt="Innayaa lookbook"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
